import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Tooltip2 } from "@blueprintjs/popover2";
import { DpmsTypes } from 'csd.phoenix.models/DpmsTypes';
import { ICustomerEgressEntityFailures } from "../../../models/CustomerEgressEntityFailures";
import { ICustomerSession } from "../../../models/CustomerSession";
import { ICustomerSummary } from "../../../models/CustomerSummary";
import routes from "../../../routePaths";
import { getCustomerSummaries } from "../../../stores/customersStore";
import DataTableContainer from "../../shared/DataTable/DataTableContainer";
import { IColumn, IColumnFilters, IColumnSort, SortDirection } from "../../shared/DataTable/models";
import useLastUpdatedTimestamp from "../../shared/useLastUpdatedTimestamp";
import { getDpmsList } from "../../shared/ListData";
import CustomerSearchInput from "./CustomerSearchInput";
import RadioFilterGroup from "./RadioFilterGroup";
import DpmsFilterGroup from "./DpmsFilterGroup";


const columns = (() => {
  const formatDate = (date?: Date) => !!date ? date.toLocaleString() : '';

  type ColumnsDef = IColumn<ICustomerSummary & { _actions_?: string }>;

  const columns: ColumnsDef[] = [
    {
      header: "Customer",
      name: "id",
      accessor: (data) => `${data.id}: ${data.name}`
    },

    {
      header: "Active?",
      name: "isActive",
    },

    {
      header: "Hosted?",
      name: "isHosted",
      disableSort: true
    },

    {
      header: "DPMS",
      name: "dpmsType",
    },

    {
      header: "Agent ID",
      name: "agentId",
    },

    {
      header: "Time Zone",
      name: "timeZoneName",
    },

    {
      header: "Last Ingress",
      name: "lastIngressSession",
      renderer: (value: ICustomerSession, row: ICustomerSummary, col: IColumn<ICustomerSummary, ICustomerSession>) => {
        if (!value) return null;
        return (
          <Tooltip2 content={<div>Session ID: {value.id}</div>}>
            {formatDate(value.createdOn)}
          </Tooltip2>)
      }
    },

    {
      header: "Last Egress",
      name: "lastEgressSession",
      renderer: (value: ICustomerSession, row: ICustomerSummary, col: IColumn<ICustomerSummary, ICustomerSession>) => {
        if (!value) return null;
        return (
          <Tooltip2 content={<div>Session ID: {value.id}</div>}>
            {formatDate(value.createdOn)}
          </Tooltip2>)
      },
    },

    {
      header: "Last Egress Error",
      name: "lastEgressEntityFailure",
      renderer: (value: ICustomerEgressEntityFailures, row: ICustomerSummary, col: IColumn<ICustomerSummary, ICustomerEgressEntityFailures>) => {
        if (!value) return null;
        return (
          <Tooltip2 content={<><div>Error count: {value.count}</div><div>Session ID: {value.sessionId}</div></>}>
            {formatDate(value.date)}
          </Tooltip2>)
        //return <span title={`Count: ${value.count}, Session ID: ${value.sessionId}`}>{formatDateString(value.date)}</span>
      }
    },

    {
      "header": "",
      name: "_actions_",
      disableSort: true,
      accessor: (data: ICustomerSummary) => data,
      renderer: (customer: ICustomerSummary) =>
        <Link
          to={generatePath(routes.support.customers.path, { customerId: customer.id })}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Button style={{ fontSize: "smaller" }} rightIcon={IconNames.CIRCLE_ARROW_RIGHT} title="Select" small={true} minimal={true}></Button>
        </Link>
    }
  ];

  return columns;
})();


const defaultSort: IColumnSort<ICustomerSummary> = {
  fieldName: "id",
  sortDirection: SortDirection.Desc
}


interface IProps {
}

interface ICustomerFilters {
  isHosted?: boolean;
  isActive?: boolean;
  customerId?: string;
  dpmsType?: DpmsTypes;
}

// Filter persistence
const defaultFilters: ICustomerFilters = { customerId: '', isActive: true };
const storageKey = "uiState.customerList.filters";
const saveFiltersToStorage = (filters: ICustomerFilters = defaultFilters) => sessionStorage.setItem(storageKey, JSON.stringify(filters));
const loadFiltersFromStorage = (): ICustomerFilters => {
  const json = sessionStorage.getItem(storageKey);
  const filters = json == null ? defaultFilters : JSON.parse(json);
  return filters as ICustomerFilters;
}


enum SearchType {
  CustomerId,
  Other
}


const CustomerPanel: React.FC<IProps> = () => {
  const [setLastUpdatedTimestamp, lastUpdatedDisplay] = useLastUpdatedTimestamp();
  const [uiFilters, setUiFilters] = useState<ICustomerFilters>({ ...defaultFilters });
  const [apiFilters, setApiFilters] = useState<ICustomerFilters | undefined>();
  const [isApiCallActive, setIsApiCallActive] = useState(false);
  const searchType = !!uiFilters.customerId ? SearchType.CustomerId : SearchType.Other;

  // Get DPMS list (excluding Sensei products)
  const dpmsList = useMemo(() => getDpmsList().filter(dpms => dpms.value !== DpmsTypes.CMP), []);

  // Save/Load filters to storage
  const filterRef = useRef(uiFilters);
  filterRef.current = uiFilters;
  useEffect(() => {
    setUiFilters(loadFiltersFromStorage());
    return () => saveFiltersToStorage(filterRef.current);
  }, []);

  // Auto-select single DPMS if only one is available (e.g., R4 for UK)
  useEffect(() => {
    if (dpmsList.length === 1) {
      const singleDpms = dpmsList[0].value as DpmsTypes;
      if (uiFilters.dpmsType !== singleDpms) {
        setUiFilters(current => ({ ...current, dpmsType: singleDpms }));
      }
    }
  }, [dpmsList, uiFilters.dpmsType]);

  const dataLoader = useCallback(async (pageNum: number, pageSize: number, sorting?: IColumnSort<ICustomerSummary>, colFilters?: IColumnFilters<ICustomerSummary>) => {
    // This method should not be called until filter is set
    if (!apiFilters) return await Promise.reject();

    setIsApiCallActive(true);
    try {
      const results = await getCustomerSummaries(apiFilters.customerId,
        apiFilters.dpmsType,
        apiFilters.isHosted,
        apiFilters.isActive,
        { pageNum, pageSize },
        sorting);
      setLastUpdatedTimestamp(new Date());
      return results;
    }
    finally {
      setIsApiCallActive(false);
    }
  }, [apiFilters, setLastUpdatedTimestamp]);


  const onCustomerIdChanged = useCallback(({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
    if (/^\d{0,10}$/.test(value)) {
      if (!!value) {
        // Reset all other filters
        setUiFilters({ customerId: value });
      }
      else {
        setUiFilters(current => ({ ...current, customerId: value }));
      }
    }
  }, []);

  const onIsActiveFlagChanged = useCallback(({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
    setUiFilters(current => ({ ...current, isActive: value === "" ? undefined : value === "1" ? true : false }))
  }, []);

  const onIsHostedFlagChanged = useCallback(({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
    setUiFilters(current => ({ ...current, isHosted: value === "" ? undefined : value === "1" ? true : false }))
  }, []);

  const onDpmsSelectionChanged = useCallback(({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
    setUiFilters(current => ({ ...current, dpmsType: value === "" ? undefined : parseInt(value) as DpmsTypes }))
  }, []);

  const runQuery = useCallback(() => {
    const customerId = uiFilters.customerId === '' ? undefined : uiFilters.customerId;
    setApiFilters({ ...uiFilters, customerId });
  }, [uiFilters]);


  return (
    <>
      <div className="columns" style={{ width: "100%" }}>
        <CustomerSearchInput
          value={uiFilters.customerId || ''}
          disabled={isApiCallActive}
          onChange={onCustomerIdChanged}
          onSearch={runQuery}
        />

        <RadioFilterGroup
          label="Status"
          selectedValue={uiFilters.isActive}
          disabled={searchType === SearchType.CustomerId}
          onChange={onIsActiveFlagChanged}
          options={[
            { value: "", label: "Any" },
            { value: "1", label: "Active" },
            { value: "0", label: "Inactive" }
          ]}
        />

        <RadioFilterGroup
          label="Location"
          selectedValue={uiFilters.isHosted}
          disabled={searchType === SearchType.CustomerId}
          onChange={onIsHostedFlagChanged}
          options={[
            { value: "", label: "Any" },
            { value: "0", label: "On-premises" },
            { value: "1", label: "Hosted" }
          ]}
        />

        <DpmsFilterGroup
          dpmsList={dpmsList}
          selectedValue={uiFilters.dpmsType}
          disabled={searchType === SearchType.CustomerId}
          onChange={onDpmsSelectionChanged}
        />
      </div>

      {lastUpdatedDisplay}

      {apiFilters && <DataTableContainer
        pageSize={20}
        columns={columns}
        dataLoader={dataLoader}
        getRowKey="id"
        defaultSort={defaultSort}
        containerStyle={{ maxHeight: 290 }}
      />}
    </>
  );
}

export default CustomerPanel;
